/**
 * Compact, AI-feel audio visualizer.
 *
 * Renders 16 bars driven by the live audio level (0–1). The level is a ~30Hz
 * signal, so it is NOT React state: the panel passes a REF (`levelRef`) and this
 * component paints the bar heights IMPERATIVELY inside its own
 * requestAnimationFrame loop — a plain DOM style write, no setState, no React
 * re-render per audio frame. That keeps the ~30Hz audio signal off React's
 * reconciler so it can never starve the host app's rendering while listening.
 * A CSS height transition smooths abrupt level changes (replacing the old
 * per-bar framer-motion spring). Variant flips the gradient between user (cyan)
 * and assistant (violet) speech. The loop runs only while `active`.
 */

import React, { useEffect, useMemo, useRef } from "react";
import { tokens } from "./styles";

const BAR_COUNT = 16;
const MIN_H = 6;
const MAX_ADD = 44;

const phaseOffsets = Array.from({ length: BAR_COUNT }, (_, i) => {
  // Symmetric, lightly randomised so adjacent bars don't move in lockstep.
  const center = (BAR_COUNT - 1) / 2;
  const dist = Math.abs(i - center) / center;
  return 0.45 + (1 - dist) * 0.55;
});

export function AudioVisualizer({ active, levelRef, variant }) {
  const palette = useMemo(() => {
    if (variant === "assistant") {
      return [tokens.accent.violet, tokens.accent.violetStrong];
    }
    return [tokens.accent.cyan, tokens.accent.cyanStrong];
  }, [variant]);

  const barsRef = useRef([]);

  useEffect(() => {
    const setHeights = (level) => {
      const bars = barsRef.current;
      for (let i = 0; i < bars.length; i += 1) {
        const el = bars[i];
        if (!el) continue;
        const h = active ? MIN_H + level * MAX_ADD * phaseOffsets[i] : MIN_H;
        el.style.height = `${h}px`;
      }
    };

    // Idle: paint a single flat frame and DON'T spin a loop.
    if (!active) {
      setHeights(0);
      return undefined;
    }

    // Active: paint from the level ref each animation frame — imperative DOM
    // only, never setState, so listening doesn't re-render the panel.
    let running = true;
    let raf = 0;
    const tick = () => {
      if (!running) return;
      const raw = levelRef && typeof levelRef.current === "number" ? levelRef.current : 0;
      setHeights(Math.max(0, Math.min(1, raw)));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      running = false;
      if (raf) cancelAnimationFrame(raf);
    };
  }, [active, levelRef]);

  return (
    <div
      role="img"
      aria-label={active ? "Live audio level" : "Audio idle"}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 4,
        height: 56,
        width: "100%",
        opacity: active ? 1 : 0.45,
        transition: "opacity 240ms ease",
      }}
    >
      {phaseOffsets.map((_, i) => (
        <span
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          ref={(el) => {
            barsRef.current[i] = el;
          }}
          style={{
            display: "inline-block",
            width: 3,
            height: MIN_H,
            borderRadius: 2,
            background: `linear-gradient(180deg, ${palette[0]} 0%, ${palette[1]} 100%)`,
            opacity: 0.85,
            // Smooths abrupt level changes now that heights are set imperatively.
            transition: "height 90ms ease-out",
          }}
        />
      ))}
    </div>
  );
}
